import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Switch,
  FormControlLabel,
  Autocomplete,
  Paper,
  Chip,
  Stack,
  alpha,
  Skeleton,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  DatePicker,
  TimePicker
} from '@mui/x-date-pickers';
import {
  Person as PersonIcon,
  Schedule as ScheduleIcon,
  LocalHospital as HospitalIcon,
  Assignment as AssignmentIcon,
  Event as EventIcon,
  CalendarToday as CalendarIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { format, addMinutes, parse, setHours, setMinutes } from 'date-fns';
import { es } from 'date-fns/locale';
import { useAuth } from '../../hooks/useAuth';
import { useNotification } from '../../contexts/NotificationContext';
import citaService from '../../services/citaService';
import pacienteService from '../../services/pacienteService';
import especialistaService from '../../services/especialistaService';
import especialidadService from '../../services/especialidadService';
import etapaTratamientoService from '../../services/etapaTratamientoService';
import PageHeader from '../../components/common/PageHeader';
import MultipleDatePicker from './MultipleDatePicker';
import AvailabilityScheduler from './AvailabilityScheduler';
import RecurrenceScheduler from './RecurrenceScheduler';
import { startOfWeek, addDays, addWeeks, isBefore } from 'date-fns';

const CitaForm = ({ isEdit = false }) => {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showNotification } = useNotification();
  
  // Estados principales
  const [loading, setLoading] = useState(isEdit);
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPacientes, setLoadingPacientes] = useState(false);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  
  const [pacienteFromUrl, setPacienteFromUrl] = useState(null);
  const [loadingPacienteFromUrl, setLoadingPacienteFromUrl] = useState(false);
  const [pacienteCargadoFromUrl, setPacienteCargadoFromUrl] = useState(false);

  // Estados para datos de selección
  const [pacientes, setPacientes] = useState([]);
  const [especialistas, setEspecialistas] = useState([]);
  const [especialidadesFiltradas, setEspecialidadesFiltradas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  
  // Estados para objetos seleccionados
  const [selectedPaciente, setSelectedPaciente] = useState(null);
  const [selectedEspecialista, setSelectedEspecialista] = useState(null);
  const [selectedEspecialidad, setSelectedEspecialidad] = useState(null);
  const [numeroSesion, setNumeroSesion] = useState(1);
  const [calculandoSesion, setCalculandoSesion] = useState(false);
  
  // Estados controlados
  const [esEventoPersonal, setEsEventoPersonal] = useState(false);
  
  // Estados para múltiples fechas
  const [multipleFechas, setMultipleFechas] = useState(false);
  const [selectedDates, setSelectedDates] = useState([]);
  const [creatingMultiple, setCreatingMultiple] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [citasToCreate, setCitasToCreate] = useState([]);
  
  //  • Estados para AvailabilityScheduler
  const [schedulerSelectedSlots, setSchedulerSelectedSlots] = useState([]);
  
  //  • Estados para citas grupales
  const [esCitaGrupal, setEsCitaGrupal] = useState(false);
  const [pacientesSeleccionados, setPacientesSeleccionados] = useState([]);
  const [grupoInfo, setGrupoInfo] = useState({
    nombre: '',
    descripcion: ''
  });

  const [esCitaRecurrente, setEsCitaRecurrente] = useState(false);
  const [patronesRecurrentes, setPatronesRecurrentes] = useState([]);
  const [fechaInicioRecurrencia, setFechaInicioRecurrencia] = useState(null);

  const [esCitaGrupalRecurrente, setEsCitaGrupalRecurrente] = useState(false);

  // Obtener fecha de URL si existe
  const searchParams = new URLSearchParams(location.search);
  const fechaFromUrl = searchParams.get('fecha'); 
  const pacienteIdFromUrl = searchParams.get('paciente');
  // ===== FUNCIONES AUXILIARES =====

  
  //  • Función para verificar si hay horarios del scheduler seleccionados
  const hasSchedulerSlots = schedulerSelectedSlots.length > 0;

  // ===== FUNCIONES DE CARGA DE DATOS =====

  // Función para cargar pacientes de un especialista
  const cargarPacientesDelEspecialista = useCallback(async (especialistaId) => {
    try {
      setLoadingPacientes(true);
 
      const response = await pacienteService.getPacientesByEspecialista(especialistaId);
      const pacientesData = Array.isArray(response.data) ? response.data : [];
      setPacientes(pacientesData);
     
    } catch (error) {
      console.error('  Error cargando pacientes:', error);
      setPacientes([]);
      showNotification('Error al cargar pacientes del especialista', 'error');
    } finally {
      setLoadingPacientes(false);
    }
  }, [showNotification]);

  // Función para cargar etapas de una especialidad
  const cargarEtapasDeEspecialidad = useCallback(async (especialidadId, pacienteId = null) => {
    try {
      setLoadingEtapas(true);
    
      
      let response;
      
      if (pacienteId) {
        response = await etapaTratamientoService.getEtapasDisponiblesPaciente(pacienteId, especialidadId);
      } else {
        response = await etapaTratamientoService.getByEspecialidad(especialidadId);
      }
      
      const etapasData = Array.isArray(response.data) ? response.data : [];
      setEtapas(etapasData);
       
    } catch (error) {
      console.error('  Error cargando etapas:', error);
      setEtapas([]);
      showNotification('Error al cargar etapas de tratamiento', 'error');
    } finally {
      setLoadingEtapas(false);
    }
  }, [showNotification]);

  const cargarPacienteDesdeUrl = useCallback(async (pacienteId) => {
  //  • PROTECCI óN: Evitar cargas múltiples
  if (loadingPacienteFromUrl || pacienteCargadoFromUrl) {
   
    return;
  }

  try {
    setLoadingPacienteFromUrl(true);
    setPacienteCargadoFromUrl(true);
 
    
    const response = await pacienteService.getPaciente(pacienteId);
    const pacienteData = response.data;
    
 
 
    
    if (!pacienteData) {
      showNotification('No se encontró el paciente seleccionado', 'error');
      return;
    }

    setPacienteFromUrl(pacienteData);
    
    //   CORREGIR: Usar la estructura real de datos
    if (pacienteData.especialistas && pacienteData.especialistas.length > 0) {
      
      const primeraAsignacion = pacienteData.especialistas[0];
 
      if (primeraAsignacion) {
 
        //   CORREGIR: Extraer el especialista real de la asignación
        const especialistaReal = primeraAsignacion.especialista;
        const especialidadReal = primeraAsignacion.especialidad;
 
        if (especialistaReal) {
          // Precargar especialista REAL
          setSelectedEspecialista(especialistaReal);
          
          // Precargar especialidades del especialista
          if (especialistaReal.especialidades) {
             setEspecialidadesFiltradas(especialistaReal.especialidades);
          } else {
            console.warn('âš ï¸ El especialista no tiene especialidades asociadas, creando array con la especialidad actual');
            // Si no hay especialidades asociadas, crear array con la especialidad actual
            if (especialidadReal) {
              setEspecialidadesFiltradas([especialidadReal]);
            }
          }
          
          // Precargar especialidad REAL
          if (especialidadReal) {
             setSelectedEspecialidad(especialidadReal);
            
            // Cargar etapas de esa especialidad
             await cargarEtapasDeEspecialidad(especialidadReal.id, pacienteData.id);
          } else {
            console.warn('âš ï¸ No se encontró especialidad en la asignación');
          }
          
          // Cargar pacientes del especialista REAL
           await cargarPacientesDelEspecialista(especialistaReal.id);
        } else {
          console.warn('âš ï¸ No se encontró especialista en la asignación');
        }
      } else {
        console.warn('âš ï¸ La primera asignación es null/undefined');
      }
    } else {
      console.warn('âš ï¸ El paciente no tiene especialistas asignados');
      
   
    }
    
    // Precargar el paciente seleccionado
    setSelectedPaciente(pacienteData);
    
     showNotification(`Datos precargados para: ${pacienteData.nombres} ${pacienteData.apellidos}`, 'success');
    
    return pacienteData;
    
  } catch (error) {
    console.error('  Error cargando paciente desde URL:', error);
    showNotification('Error al cargar los datos del paciente', 'error');
    setPacienteCargadoFromUrl(false);
    return null;
  } finally {
    setLoadingPacienteFromUrl(false);
  }
}, [cargarEtapasDeEspecialidad, cargarPacientesDelEspecialista, showNotification, loadingPacienteFromUrl, pacienteCargadoFromUrl]);
  
  // Función para calcular número de sesión
  const calcularNumeroSesion = useCallback(async (pacienteId, especialidadId, etapaId) => {
    try {
      setCalculandoSesion(true);
     
      const response = await citaService.getSesionesEtapa(pacienteId, especialidadId, etapaId);
      const numeroCalculado = response.data?.siguiente_numero || 1;
      setNumeroSesion(numeroCalculado);
       
    } catch (error) {
      console.error('  Error calculando sesión:', error);
      setNumeroSesion(1);
    } finally {
      setCalculandoSesion(false);
    }
  }, []);

  // ===== HANDLERS DE EVENTOS =====

  // Handler para evento personal
  const handleEventoPersonalChange = (event) => {
    const isEventoPersonal = event.target.checked;
    
    
    setEsEventoPersonal(isEventoPersonal);
    formik.setFieldValue('es_evento_personal', isEventoPersonal);
    
    if (isEventoPersonal) {
      formik.setFieldValue('paciente_id', '');
      formik.setFieldValue('especialidad_id', '');
      formik.setFieldValue('etapa_id', '');
      formik.setFieldValue('tipo', 'Otro');
      setSelectedPaciente(null);
      setSelectedEspecialidad(null);
      setPacientes([]);
      setEtapas([]);
      setEspecialidadesFiltradas([]);
      
      // Limpiar slots del scheduler
      setSchedulerSelectedSlots([]);
    } else {
      formik.setFieldValue('tipo', 'Seguimiento');
      if (selectedEspecialista?.id) {
        if (selectedEspecialista.especialidades) {
          setEspecialidadesFiltradas(selectedEspecialista.especialidades);
        }
        cargarPacientesDelEspecialista(selectedEspecialista.id);
      }
    }
  };

const determinarTipoCreacion = () => {
   

  // Prioridad 1: Citas grupales recurrentes
  if (esCitaGrupalRecurrente && patronesRecurrentes.length > 0) {
    return 'grupal-recurrente';
  }

  // Prioridad 2: Citas recurrentes individuales
  if (esCitaRecurrente && patronesRecurrentes.length > 0) {
    return 'individual-recurrente';
  }

  // Prioridad 3: Citas grupales normales
  if ((esCitaGrupal || esCitaGrupalRecurrente) && pacientesSeleccionados.length > 0) {
    if (hasSchedulerSlots) {
      return 'grupal-scheduler';
    } else if (multipleFechas && selectedDates.length > 0) {
      return 'grupal-multiple';
    } else {
      return 'grupal-simple';
    }
  }

  // Prioridad 4: Citas individuales
  if (hasSchedulerSlots) {
    return 'scheduler';
  } else if (multipleFechas && selectedDates.length > 0) {
    return 'multiple-fechas';
  } else {
    return 'individual';
  }
};

  // Handler para cambio de especialista
  const handleEspecialistaChange = (event, newValue) => {
     
    setSelectedEspecialista(newValue);
    formik.setFieldValue('especialista_id', newValue?.id || '');
    
    if (newValue?.especialidades) {
      setEspecialidadesFiltradas(newValue.especialidades);
    } else {
      setEspecialidadesFiltradas([]);
    }
    
    formik.setFieldValue('paciente_id', '');
    formik.setFieldValue('especialidad_id', '');
    formik.setFieldValue('etapa_id', '');
    setSelectedPaciente(null);
    setSelectedEspecialidad(null);
    setPacientes([]);
    setEtapas([]);
    
    // Limpiar slots del scheduler cuando cambia especialista
    setSchedulerSelectedSlots([]);
    
    if (newValue?.id && !esEventoPersonal) {
      cargarPacientesDelEspecialista(newValue.id);
    }
  };

  // Handler para cambio de paciente
  const handlePacienteChange = (event, newValue) => {
     
    setSelectedPaciente(newValue);
    formik.setFieldValue('paciente_id', newValue?.id || '');
    
    if (newValue?.id && formik.values.especialidad_id) {
      cargarEtapasDeEspecialidad(formik.values.especialidad_id, newValue.id);
      formik.setFieldValue('etapa_id', '');
    }
    
    if (newValue?.id && formik.values.especialidad_id && formik.values.etapa_id) {
      calcularNumeroSesion(newValue.id, formik.values.especialidad_id, formik.values.etapa_id);
    }
  };

  // Handler para cambio de especialidad
  const handleEspecialidadChange = (event) => {
    const especialidadId = event.target.value;
   
    
    formik.setFieldValue('especialidad_id', especialidadId);
    
    const especialidad = especialidadesFiltradas.find(e => e.id === Number(especialidadId));
    setSelectedEspecialidad(especialidad);
    
    formik.setFieldValue('etapa_id', '');
    setEtapas([]);
    
    if (especialidadId) {
      const pacienteId = selectedPaciente?.id || null;
      cargarEtapasDeEspecialidad(Number(especialidadId), pacienteId);
    }
    
    if (formik.values.hora_inicio) {
      const horaFin = addMinutes(formik.values.hora_inicio, 60);
      formik.setFieldValue('hora_fin', horaFin);
    }
  };

  // Handler para cambio de etapa
  const handleEtapaChange = (event) => {
    const etapaId = event.target.value;
     
    formik.setFieldValue('etapa_id', etapaId);
    
    if (formik.values.paciente_id && formik.values.especialidad_id && etapaId) {
      calcularNumeroSesion(formik.values.paciente_id, formik.values.especialidad_id, etapaId);
    }
  };

  // Handler para hora de inicio
  const handleHoraInicioChange = (hora) => {
    formik.setFieldValue('hora_inicio', hora);
    
    if (hora) {
      // Siempre agregar 1 hora exacta
      const horaFin = addMinutes(hora, 60);
      formik.setFieldValue('hora_fin', horaFin);
    }
  };
 
  // Manejar cambio de fechas múltiples
  const handleMultipleDatesChange = (dates) => {
    setSelectedDates(dates);
    formik.setFieldValue('selectedDates', dates);
  };

  // ===== FUNCIONES DE CREACI óN =====

  // Función para crear cita individual (funcionalidad existente)
  const handleCreateSingleCita = async (values) => {
  try {
 
    
    const formattedValues = {
      ...values,
      es_evento_personal: esEventoPersonal,
      fecha: values.fecha ? format(values.fecha, 'yyyy-MM-dd') : null,
      hora_inicio: values.hora_inicio ? format(values.hora_inicio, 'HH:mm:ss') : null, // âœ… Formato 24h
      hora_fin: values.hora_fin ? format(values.hora_fin, 'HH:mm:ss') : null, // âœ… Formato 24h
      ...(esEventoPersonal ? {
        paciente_id: null,
        especialidad_id: null,
        etapa_id: null,
        tipo: formik.values.tipo || 'Otro'
      } : {})
    };
     
    
    if (isEdit) {
      await citaService.updateCita(id, formattedValues);
      showNotification('Cita actualizada con Ã©xito', 'success');
    } else {
      await citaService.createCita(formattedValues);
      showNotification(
        esEventoPersonal ? 'Evento personal creado con Ã©xito' : 'Cita creada con Ã©xito', 
        'success'
      );
    }
    
    navigate('/citas');
  } catch (error) {
    console.error('  Error:', error);
    showNotification(
      `Error al ${isEdit ? 'actualizar' : 'crear'} la ${esEventoPersonal ? 'evento' : 'cita'}`,
      'error'
    );
  }
};

const handleCreateCitaGrupal = async (values) => {
  try {
 
    // âœ… VALIDACIONES CRÃTICAS
    if (!pacientesSeleccionados || pacientesSeleccionados.length === 0) {
      showNotification('Debe seleccionar al menos un paciente para la cita grupal', 'error');
      return;
    }
    
    if (!values.especialista_id) {
      showNotification('Debe seleccionar un especialista para la cita grupal', 'error');
      return;
    }
    
    if (!values.especialidad_id) {
      showNotification('Debe seleccionar una especialidad para la cita grupal', 'error');
      return;
    }
    
    // âœ… Validación de fechas/horarios según el modo
    if (hasSchedulerSlots) {
      if (schedulerSelectedSlots.length === 0) {
        showNotification('Debe seleccionar al menos un horario del calendario', 'error');
        return;
      }
    } else if (multipleFechas) {
      if (selectedDates.length === 0) {
        showNotification('Debe seleccionar al menos una fecha', 'error');
        return;
      }
      if (!values.hora_inicio || !values.hora_fin) {
        showNotification('Debe seleccionar horarios para las citas grupales', 'error');
        return;
      }
    } else {
      if (!values.fecha) {
        showNotification('Debe seleccionar una fecha para la cita grupal', 'error');
        return;
      }
      if (!values.hora_inicio || !values.hora_fin) {
        showNotification('Debe seleccionar horarios para la cita grupal', 'error');
        return;
      }
    }
    
    const pacientesIds = pacientesSeleccionados.map(p => p.id);
 
    //  • MANEJAR DIFERENTES MODOS DE CITA GRUPAL
    if (hasSchedulerSlots) {
      // ðŸŽ¯ CITA GRUPAL DESDE SCHEDULER (múltiples horarios específicos)
      await handleCreateCitaGrupalFromScheduler(values, pacientesIds);
    } else if (multipleFechas && selectedDates.length > 0) {
      // ðŸŽ¯ CITA GRUPAL EN MÃšLTIPLES FECHAS
      await handleCreateCitaGrupalMultipleFechas(values, pacientesIds);
    } else {
      // ðŸŽ¯ CITA GRUPAL SIMPLE (una fecha)
      await handleCreateCitaGrupalSimple(values, pacientesIds);
    }
    
  } catch (error) {
    console.error('  Error creando cita grupal:', error);
    const errorMessage = error.response?.data?.message || 'Error al crear la cita grupal';
    showNotification(errorMessage, 'error');
  }
};

//  • FUNCI óN AUXILIAR: Cita grupal simple
const handleCreateCitaGrupalSimple = async (values, pacientesIds) => {
  const citaGrupalData = {
    pacientes_ids: pacientesIds,
    especialista_id: values.especialista_id,
    especialidad_id: values.especialidad_id,
    etapa_id: values.etapa_id || null,
    fecha: format(values.fecha, 'yyyy-MM-dd'),
    hora_inicio: format(values.hora_inicio, 'HH:mm:ss'),
    hora_fin: format(values.hora_fin, 'HH:mm:ss'),
    tipo: values.tipo || 'Seguimiento',
    estado: values.estado || 'Programada',
    motivo: values.motivo || `Cita grupal - ${pacientesIds.length} pacientes`,
    notas: values.notas || '',
    grupo_info: grupoInfo.nombre ? {
      nombre: grupoInfo.nombre,
      descripcion: grupoInfo.descripcion
    } : null
  };
 
  const response = await citaService.createCitaGrupal(citaGrupalData);
  showNotification(`Cita grupal creada exitosamente para ${pacientesIds.length} pacientes`, 'success');
  navigate('/citas');
};

//  • FUNCI óN AUXILIAR: Citas grupales en múltiples fechas
const handleCreateCitaGrupalMultipleFechas = async (values, pacientesIds) => {
  setCreatingMultiple(true);
  
  try {
    let citasCreadas = 0;
    let errores = [];
    
    for (const fecha of selectedDates) {
      try {
        const citaGrupalData = {
          pacientes_ids: pacientesIds,
          especialista_id: values.especialista_id,
          especialidad_id: values.especialidad_id,
          etapa_id: values.etapa_id || null,
          fecha: format(fecha, 'yyyy-MM-dd'),
          hora_inicio: format(values.hora_inicio, 'HH:mm:ss'),
          hora_fin: format(values.hora_fin, 'HH:mm:ss'),
          tipo: values.tipo || 'Seguimiento',
          estado: values.estado || 'Programada',
          motivo: values.motivo || `Cita grupal - ${pacientesIds.length} pacientes`,
          notas: values.notas || '',
          grupo_info: grupoInfo.nombre ? {
            nombre: `${grupoInfo.nombre} - ${format(fecha, 'dd/MM/yyyy')}`,
            descripcion: grupoInfo.descripcion
          } : null
        };
        
   
        await citaService.createCitaGrupal(citaGrupalData);
        citasCreadas++;
        
        // PequeÃ±a pausa entre requests
        if (citasCreadas < selectedDates.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error(`  Error en fecha ${format(fecha, 'dd/MM/yyyy')}:`, error);
        errores.push({
          fecha: format(fecha, 'dd/MM/yyyy'),
          error: error.response?.data?.message || 'Error desconocido'
        });
      }
    }
    
    // Mostrar resultados
    if (citasCreadas > 0) {
      const mensaje = errores.length > 0 
        ? `${citasCreadas} citas grupales creadas exitosamente. ${errores.length} con errores.`
        : `${citasCreadas} citas grupales creadas exitosamente para ${pacientesIds.length} pacientes`;
      
      showNotification(mensaje, errores.length > 0 ? 'warning' : 'success');
      navigate('/citas');
    } else {
      throw new Error('No se pudo crear ninguna cita grupal');
    }
  } finally {
    setCreatingMultiple(false);
  }
};

//  • FUNCI óN AUXILIAR: Citas grupales desde scheduler
const handleCreateCitaGrupalFromScheduler = async (values, pacientesIds) => {
  setCreatingMultiple(true);
  
  try {
    const dateTimes = convertSlotsToDateTimes(schedulerSelectedSlots);
    const duracionDefecto = selectedEspecialidad?.duracion_cita_defecto || 60;
    
    let citasCreadas = 0;
    let errores = [];
    
    for (const dateTime of dateTimes) {
      try {
        const citaGrupalData = {
          pacientes_ids: pacientesIds,
          especialista_id: values.especialista_id,
          especialidad_id: values.especialidad_id,
          etapa_id: values.etapa_id || null,
          fecha: format(dateTime, 'yyyy-MM-dd'),
          hora_inicio: format(dateTime, 'HH:mm:ss'),
          hora_fin: format(addMinutes(dateTime, duracionDefecto), 'HH:mm:ss'),
          tipo: values.tipo || 'Seguimiento',
          estado: values.estado || 'Programada',
          motivo: values.motivo || `Cita grupal - ${pacientesIds.length} pacientes`,
          notas: values.notas || '',
          grupo_info: grupoInfo.nombre ? {
            nombre: `${grupoInfo.nombre} - ${format(dateTime, 'dd/MM/yyyy HH:mm')}`,
            descripcion: grupoInfo.descripcion
          } : null
        };
        
   
        await citaService.createCitaGrupal(citaGrupalData);
        citasCreadas++;
        
        // PequeÃ±a pausa entre requests
        if (citasCreadas < dateTimes.length) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
      } catch (error) {
        console.error(`  Error en horario ${format(dateTime, 'dd/MM/yyyy HH:mm')}:`, error);
        errores.push({
          fecha: format(dateTime, 'dd/MM/yyyy HH:mm'),
          error: error.response?.data?.message || 'Error desconocido'
        });
      }
    }
    
    // Mostrar resultados
    if (citasCreadas > 0) {
      const mensaje = errores.length > 0 
        ? `${citasCreadas} citas grupales creadas desde calendario. ${errores.length} con errores.`
        : `${citasCreadas} citas grupales creadas exitosamente desde calendario para ${pacientesIds.length} pacientes`;
      
      showNotification(mensaje, errores.length > 0 ? 'warning' : 'success');
      navigate('/citas');
    } else {
      throw new Error('No se pudo crear ninguna cita grupal desde el calendario');
    }
  } finally {
    setCreatingMultiple(false);
  }
};

//  • FUNCI óN: Crear citas recurrentes
//  • FUNCI óN: Crear citas recurrentes
const handleCreateCitasRecurrentes = async (values) => {
  try {
    // Validaciones
    if (!fechaInicioRecurrencia) {
      showNotification('Debe seleccionar una fecha de inicio para la recurrencia', 'error');
      return;
    }

    if (patronesRecurrentes.length === 0) {
      showNotification('Debe configurar al menos un patrón de recurrencia', 'error');
      return;
    }

    // âœ… VALIDACIONES MEJORADAS PARA INCLUIR MODO GRUPAL RECURRENTE
    if (!esCitaGrupalRecurrente && !esEventoPersonal && !selectedPaciente) {
      showNotification('Debe seleccionar un paciente', 'error');
      return;
    }

    if (esCitaGrupalRecurrente && pacientesSeleccionados.length === 0) {
      showNotification('Debe seleccionar al menos un paciente para las citas grupales recurrentes', 'error');
      return;
    }

    if (!values.especialista_id) {
      showNotification('Debe seleccionar un especialista', 'error');
      return;
    }

    if (!esEventoPersonal && !values.especialidad_id) {
      showNotification('Debe seleccionar una especialidad', 'error');
      return;
    }

    setCreatingMultiple(true);

    // Generar todas las fechas basadas en los patrones
    const todasLasCitas = generarCitasDesdePatrones();
    
    if (todasLasCitas.length === 0) {
      showNotification('No se generaron fechas vÃ¡lidas con los patrones configurados', 'error');
      return;
    }

    // âœ… DECIDIR QUÃ‰ FUNCI óN USAR SEGÃšN EL MODO
    if (esCitaGrupalRecurrente) {
      await crearCitasGrupalesRecurrentes(values, todasLasCitas);
    } else {
      await crearCitasRecurrentesIndividuales(values, todasLasCitas);
    }

  } catch (error) {
    console.error('Error creando citas recurrentes:', error);
    showNotification(`Error al crear las ${esEventoPersonal ? 'eventos' : 'citas'} recurrentes: ${error.message}`, 'error');
  } finally {
    setCreatingMultiple(false);
  }
};

const crearCitasGrupalesRecurrentes = async (values, todasLasCitas) => {
  let sesionesCreadas = 0;
  let errores = [];
  const pacientesIds = pacientesSeleccionados.map(p => p.id);
 

  // âœ… USAR EL ENDPOINT ESPECIALIZADO DEL BACKEND
  try {
    // Preparar datos para el endpoint especializado
    const citas_programadas = todasLasCitas.map(citaInfo => ({
      fecha: format(citaInfo.fecha, 'yyyy-MM-dd'),
      hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
      hora_fin: format(citaInfo.horaFin, 'HH:mm:ss')
    }));

    const datosParaEnviar = {
      citas_programadas: citas_programadas,
      pacientes_seleccionados: pacientesIds,
      nombre_grupo: grupoInfo.nombre || `Grupo Recurrente ${format(new Date(), 'MMM yyyy')}`,
      descripcion_grupo: grupoInfo.descripcion || 'Citas grupales recurrentes creadas automÃ¡ticamente',
      especialista_id: values.especialista_id,
      especialidad_id: values.especialidad_id,
      etapa_id: values.etapa_id || null,
      programa_id: values.programa_id || null,
      tipo: values.tipo || 'Seguimiento',
      estado: 'Programada',
      motivo: values.motivo || 'Cita grupal recurrente',
      notas: values.notas || 'Creada mediante patrón recurrente'
    };

 
    // âœ… LLAMAR AL ENDPOINT ESPECIALIZADO
    const response = await citaService.crearCitasGrupalesRecurrentes(datosParaEnviar);
    
 
    // Mostrar resultado exitoso
    const mensaje = `${response.data.totalCitas} citas grupales recurrentes creadas exitosamente para ${pacientesIds.length} pacientes en ${response.data.totalSesiones} sesiones`;
    
    showNotification(mensaje, 'success');
    navigate('/citas');

  } catch (error) {
    console.error('  ERROR en citas grupales recurrentes:', error);
    
    // Si el endpoint especializado falla, mostrar error específico
    const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
    showNotification(`Error creando citas grupales recurrentes: ${errorMessage}`, 'error');
    
    // Opcional: Fallback a creación individual
  
    await crearCitasGrupalesRecurrentesFallback(values, todasLasCitas, pacientesIds);
  }
};

const crearCitasGrupalesRecurrentesFallback = async (values, todasLasCitas, pacientesIds) => {
  let sesionesCreadas = 0;
  let errores = [];

  for (const citaInfo of todasLasCitas) {
    try {
      const citaGrupalData = {
        pacientes_ids: pacientesIds,
        especialista_id: values.especialista_id,
        especialidad_id: values.especialidad_id,
        etapa_id: values.etapa_id || null,
        fecha: format(citaInfo.fecha, 'yyyy-MM-dd'),
        hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
        hora_fin: format(citaInfo.horaFin, 'HH:mm:ss'),
        tipo: values.tipo || 'Seguimiento',
        estado: 'Programada',
        motivo: values.motivo || `Cita grupal recurrente - ${citaInfo.patron.diaNombre} - Sesión ${citaInfo.numeroSesion}`,
        notas: values.notas ? `${values.notas} | Patrón: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}` : `Patrón recurrente grupal: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}`,
        grupo_info: grupoInfo.nombre ? {
          nombre: `${grupoInfo.nombre} - ${citaInfo.patron.diaNombre} S${citaInfo.numeroSesion}`,
          descripcion: `${grupoInfo.descripcion} | Cita recurrente`
        } : {
          nombre: `Grupo Recurrente ${citaInfo.patron.diaNombre}`,
          descripcion: `Cita grupal recurrente - Sesión ${citaInfo.numeroSesion}`
        }
      };

      await citaService.createCitaGrupal(citaGrupalData);
      sesionesCreadas++;

      // Pausa entre requests
      if (sesionesCreadas % 3 === 0) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }

    } catch (error) {
      console.error(`Error en cita grupal ${format(citaInfo.fecha, 'dd/MM/yyyy HH:mm')}:`, error);
      errores.push({
        fecha: format(citaInfo.fecha, 'dd/MM/yyyy HH:mm'),
        error: error.response?.data?.message || 'Error desconocido'
      });
    }
  }

  // Mostrar resultados del fallback
  if (sesionesCreadas > 0) {
    const mensaje = errores.length > 0 
      ? `${sesionesCreadas} citas grupales recurrentes creadas (fallback). ${errores.length} con errores.`
      : `${sesionesCreadas} citas grupales recurrentes creadas exitosamente para ${pacientesIds.length} pacientes (fallback)`;

    showNotification(mensaje, errores.length > 0 ? 'warning' : 'success');
    navigate('/citas');
  } else {
    throw new Error('No se pudo crear ninguna cita grupal recurrente');
  }
};

// Función auxiliar: Generar fechas desde patrones
// Función auxiliar: Generar fechas desde patrones
const generarCitasDesdePatrones = () => {
 

  const todasLasCitas = [];

  patronesRecurrentes.forEach((patron, patronIndex) => {
 
    let fechaActual = startOfWeek(fechaInicioRecurrencia, { weekStartsOn: 1 });
    
    // Ajustar al día correcto de la semana
    fechaActual = addDays(fechaActual, patron.diaSemana === 0 ? 6 : patron.diaSemana - 1);
    
    // Si la fecha es anterior a inicio, mover a siguiente semana
    if (isBefore(fechaActual, fechaInicioRecurrencia)) {
      fechaActual = addWeeks(fechaActual, 1);
    }
 
    // Generar todas las sesiones
    for (let i = 0; i < patron.cantidadSesiones; i++) {
      const fechaCita = new Date(fechaActual);
      fechaCita.setHours(patron.horaInicio.getHours(), patron.horaInicio.getMinutes(), 0, 0);

      const fechaFin = new Date(fechaCita);
      fechaFin.setHours(patron.horaFin.getHours(), patron.horaFin.getMinutes(), 0, 0);

      todasLasCitas.push({
        fecha: fechaCita,
        horaInicio: fechaCita,
        horaFin: fechaFin,
        patron: patron,
        numeroSesion: i + 1
      });

 
      fechaActual = addWeeks(fechaActual, 1);
    }
  });

  const citasOrdenadas = todasLasCitas.sort((a, b) => a.fecha - b.fecha);
  
 

  return citasOrdenadas;
};

// Función auxiliar: Crear citas recurrentes individuales
// Función auxiliar: Crear citas recurrentes individuales
const crearCitasRecurrentesIndividuales = async (values, todasLasCitas) => {
  let citasCreadas = 0;
  let errores = [];

 
  for (let i = 0; i < todasLasCitas.length; i++) {
    const citaInfo = todasLasCitas[i];
    
    try {
 

      const citaData = {
        // Para eventos personales, omitir paciente_id
        ...(esEventoPersonal ? {} : { paciente_id: values.paciente_id }),
        especialista_id: values.especialista_id,
        especialidad_id: esEventoPersonal ? null : values.especialidad_id,
        etapa_id: esEventoPersonal ? null : (values.etapa_id || null),
        fecha: format(citaInfo.fecha, 'yyyy-MM-dd'),
        hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
        hora_fin: format(citaInfo.horaFin, 'HH:mm:ss'),
        tipo: esEventoPersonal ? (values.tipo || 'Otro') : (values.tipo || 'Seguimiento'),
        estado: 'Programada',
        motivo: values.motivo || (esEventoPersonal 
          ? `Evento recurrente - ${citaInfo.patron.diaNombre} - Sesión ${citaInfo.numeroSesion}`
          : `Cita recurrente - ${citaInfo.patron.diaNombre} - Sesión ${citaInfo.numeroSesion}`
        ),
        notas: values.notas ? `${values.notas} | Patrón: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}` : `Patrón recurrente: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}`,
        es_evento_personal: esEventoPersonal
      };

   
      const response = await citaService.createCita(citaData);
      citasCreadas++;
      
    
      // Pausa pequeÃ±a entre requests para no sobrecargar
      if (citasCreadas % 5 === 0 && i < todasLasCitas.length - 1) {
         await new Promise(resolve => setTimeout(resolve, 200));
      }

    } catch (error) {
      console.error(`  Error en cita ${i + 1} (${format(citaInfo.fecha, 'dd/MM/yyyy HH:mm')}):`, error);
      errores.push({
        fecha: format(citaInfo.fecha, 'dd/MM/yyyy HH:mm'),
        patron: citaInfo.patron.diaNombre,
        sesion: citaInfo.numeroSesion,
        error: error.response?.data?.message || error.message || 'Error desconocido'
      });
    }
  }
 

  // Mostrar resultados
  if (citasCreadas > 0) {
    const tipoEvento = esEventoPersonal ? 'eventos recurrentes' : 'citas recurrentes';
    const mensaje = errores.length > 0 
      ? `${citasCreadas} ${tipoEvento} creadas. ${errores.length} con errores.`
      : `${citasCreadas} ${tipoEvento} creadas exitosamente`;

    showNotification(mensaje, errores.length > 0 ? 'warning' : 'success');
    
 
    
    navigate('/citas');
  } else {
    throw new Error(`No se pudo crear ninguna ${esEventoPersonal ? 'evento' : 'cita'} recurrente`);
  }
};

// Función auxiliar: Crear citas recurrentes grupales
const crearCitasRecurrentesGrupales = async (values, todasLasCitas) => {
  let citasCreadas = 0;
  let errores = [];
  const pacientesIds = pacientesSeleccionados.map(p => p.id);

  for (const citaInfo of todasLasCitas) {
    try {
      const citaGrupalData = {
        pacientes_ids: pacientesIds,
        especialista_id: values.especialista_id,
        especialidad_id: values.especialidad_id,
        etapa_id: values.etapa_id || null,
        fecha: format(citaInfo.fecha, 'yyyy-MM-dd'),
        hora_inicio: format(citaInfo.horaInicio, 'HH:mm:ss'),
        hora_fin: format(citaInfo.horaFin, 'HH:mm:ss'),
        tipo: values.tipo || 'Seguimiento',
        estado: 'Programada',
        motivo: values.motivo || `Cita grupal recurrente - ${citaInfo.patron.diaNombre} - Sesión ${citaInfo.numeroSesion}`,
        notas: values.notas ? `${values.notas} | Patrón: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}` : `Patrón recurrente grupal: ${citaInfo.patron.diaNombre} sesión ${citaInfo.numeroSesion}`,
        grupo_info: grupoInfo.nombre ? {
          nombre: `${grupoInfo.nombre} - ${citaInfo.patron.diaNombre} S${citaInfo.numeroSesion}`,
          descripcion: `${grupoInfo.descripcion} | Cita recurrente`
        } : {
          nombre: `Grupo Recurrente ${citaInfo.patron.diaNombre}`,
          descripcion: `Cita grupal recurrente - Sesión ${citaInfo.numeroSesion}`
        }
      };

      await citaService.createCitaGrupal(citaGrupalData);
      citasCreadas++;

      // Pausa entre requests
      if (citasCreadas % 3 === 0) {
        await new Promise(resolve => setTimeout(resolve, 150));
      }

    } catch (error) {
      console.error(`  Error en cita grupal ${format(citaInfo.fecha, 'dd/MM/yyyy HH:mm')}:`, error);
      errores.push({
        fecha: format(citaInfo.fecha, 'dd/MM/yyyy HH:mm'),
        error: error.response?.data?.message || 'Error desconocido'
      });
    }
  }

  // Mostrar resultados
  const mensaje = errores.length > 0 
    ? `${citasCreadas} citas grupales recurrentes creadas. ${errores.length} con errores.`
    : `${citasCreadas} citas grupales recurrentes creadas exitosamente para ${pacientesIds.length} pacientes`;

  showNotification(mensaje, errores.length > 0 ? 'warning' : 'success');
  
  if (citasCreadas > 0) {
    navigate('/citas');
  }
};

const verificarFormato24h = (timeString) => {
  
  
  if (!timeString) {
   
    return false;
  }
  
  // Regex para formato HH:mm:ss en 24h
  const regex24h = /^([01]?[0-9]|2[0-3]):[0-5][0-9]:[0-5][0-9]$/;
  const isValid = regex24h.test(timeString);
  
 
  
  if (isValid) {
    const [hours] = timeString.split(':');
 
  }
  
  return isValid;
};

const convertSlotsToDateTimes = (slotKeys) => {
 
  
  return slotKeys.map((slotKey, index) => {
  
    
    try {
      // âœ… SOLUCI óN: Usar lastIndexOf para separar correctamente
      const lastDashIndex = slotKey.lastIndexOf('-');
      
      if (lastDashIndex === -1) {
        throw new Error('Formato de slotKey invÃ¡lido - no contiene guiones');
      }
      
      const dateStr = slotKey.substring(0, lastDashIndex);
      const hour = slotKey.substring(lastDashIndex + 1);
      
 
      
      // Validar que dateStr y hour existan y tengan formato correcto
      if (!dateStr || !hour) {
        throw new Error('dateStr o hour es vacío');
      }
      
      if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
        throw new Error(`Formato de fecha invÃ¡lido: ${dateStr}`);
      }
      
      if (!/^\d{1,2}$/.test(hour)) {
        throw new Error(`Formato de hora invÃ¡lido: ${hour}`);
      }
      
      // Crear fecha mÃ¡s segura
      const [year, month, day] = dateStr.split('-').map(Number);
 
      
      // Validar números
      if (isNaN(year) || isNaN(month) || isNaN(day) || isNaN(parseInt(hour))) {
        throw new Error('Valores NaN detectados');
      }
      
      // Validar rangos
      if (year < 2024 || year > 2030) throw new Error(`AÃ±o fuera de rango: ${year}`);
      if (month < 1 || month > 12) throw new Error(`Mes fuera de rango: ${month}`);
      if (day < 1 || day > 31) throw new Error(`Día fuera de rango: ${day}`);
      
      const hourNum = parseInt(hour);
      if (hourNum < 0 || hourNum > 23) throw new Error(`Hora fuera de rango: ${hourNum}`);
      
      // ðŸš¨ CRÃTICO: Crear fecha con hora en formato 24h
      const date = new Date(year, month - 1, day, hourNum, 0, 0, 0);
 
      
      // Verificar que la fecha sea vÃ¡lida
      if (isNaN(date.getTime())) {
        throw new Error('Fecha invÃ¡lida creada');
      }
      
      // ðŸš¨ VERIFICACI óN CRÃTICA: Confirmar formato 24h
      if (date.getFullYear() !== year || 
          date.getMonth() !== month - 1 || 
          date.getDate() !== day ||
          date.getHours() !== hourNum) {
        throw new Error('Fecha inconsistente despuÃ©s de creación');
      }
 
      return date;
    } catch (error) {
      console.error(`  ERROR en convertSlotsToDateTimes para slot ${index + 1}:`, error);
      console.error('  SlotKey problemÃ¡tico:', slotKey);
      throw new Error(`Error procesando slot ${slotKey}: ${error.message}`);
    }
  });
};

// ========================================
// 2. FUNCI óN ACTUALIZADA: handleCreateMultipleCitasFromScheduler (CitaForm.jsx)
// ========================================

const handleCreateMultipleCitasFromScheduler = async (values) => {
  try {
 
    setCreatingMultiple(true);

    const dateTimes = convertSlotsToDateTimes(schedulerSelectedSlots);
  

    const baseData = {
      ...values,
      es_evento_personal: esEventoPersonal,
      ...(esEventoPersonal ? {
        paciente_id: null,
        especialidad_id: null,
        etapa_id: null,
        tipo: values.tipo || 'Otro'
      } : {})
    };

    // Calcular duración por defecto
    const duracionDefecto = selectedEspecialidad?.duracion_cita_defecto || 60;
    
    // ðŸš¨ CRÃTICO: Formatear horarios en formato 24h para backend
    const horarios_especificos = dateTimes.map(dateTime => {
      const fechaFormateada = format(dateTime, 'yyyy-MM-dd');
      const horaInicioFormateada = format(dateTime, 'HH:mm:ss'); // âœ… Formato 24h
      const horaFinFormateada = format(addMinutes(dateTime, duracionDefecto), 'HH:mm:ss'); // âœ… Formato 24h
      
    
      
      return {
        fecha: fechaFormateada,
        hora_inicio: horaInicioFormateada,
        hora_fin: horaFinFormateada
      };
    });
    
    // Formatear datos para endpoint de múltiples citas
    const dataToSend = {
      ...baseData,
      fechas: dateTimes.map(dateTime => format(dateTime, 'yyyy-MM-dd')),
      hora_inicio: dateTimes.length > 0 ? format(dateTimes[0], 'HH:mm:ss') : null, // âœ… Formato 24h
      hora_fin: dateTimes.length > 0 ? format(addMinutes(dateTimes[0], duracionDefecto), 'HH:mm:ss') : null, // âœ… Formato 24h
      horarios_especificos: horarios_especificos
    };
 

    // Resto del código de envío...
    if (citaService.createMultiplesCitasConHorarios) {
      const response = await citaService.createMultiplesCitasConHorarios(dataToSend);
      
      if (response.data.conflictos && response.data.conflictos.length > 0) {
        showConflictDialog(response.data);
      } else {
        showNotification(
          `${response.data.totalCreadas} ${esEventoPersonal ? 'eventos' : 'citas'} creadas exitosamente desde el calendario`,
          'success'
        );
        navigate('/citas');
      }
    } else {
      // Fallback: crear citas individuales
      const citasCreadas = [];
      const citasConError = [];

      for (let i = 0; i < dateTimes.length; i++) {
        const dateTime = dateTimes[i];
        try {
          const citaData = {
            ...baseData,
            fecha: format(dateTime, 'yyyy-MM-dd'),
            hora_inicio: format(dateTime, 'HH:mm:ss'), // âœ… Formato 24h
            hora_fin: format(addMinutes(dateTime, duracionDefecto), 'HH:mm:ss') // âœ… Formato 24h
          };

     
          
          const response = await citaService.createCita(citaData);
          citasCreadas.push(response.data);
       

          if (i < dateTimes.length - 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (citaError) {
          console.error(`  Error creando cita ${i + 1}:`, citaError);
          citasConError.push({
            fecha: format(dateTime, 'dd/MM/yyyy HH:mm'),
            error: citaError.response?.data?.message || 'Error desconocido'
          });
        }
      }

      // Mostrar resultados
      if (citasCreadas.length > 0) {
        const mensaje = citasConError.length > 0 
          ? `${citasCreadas.length} citas creadas exitosamente. ${citasConError.length} citas con errores.`
          : `${citasCreadas.length} ${esEventoPersonal ? 'eventos' : 'citas'} creadas exitosamente desde el calendario`;
        
        showNotification(mensaje, citasConError.length > 0 ? 'warning' : 'success');
        navigate('/citas');
      } else {
        throw new Error('No se pudo crear ninguna cita. Todos los horarios tienen conflictos.');
      }
    }
  } catch (error) {
    console.error('  Error creando citas desde scheduler:', error);
    const errorMessage = error.response?.data?.message || error.message || 'Error desconocido';
    showNotification(
      `Error al crear las ${esEventoPersonal ? 'eventos' : 'citas'} desde el calendario: ${errorMessage}`,
      'error'
    );
  } finally {
    setCreatingMultiple(false);
  }
};


  // Función para crear múltiples citas (funcionalidad original)
  const handleCreateMultipleCitas = async (values) => {
    try {
  
      setCreatingMultiple(true);

      const baseData = {
        ...values,
        es_evento_personal: esEventoPersonal,
        hora_inicio: values.hora_inicio ? format(values.hora_inicio, 'HH:mm:ss') : null,
        hora_fin: values.hora_fin ? format(values.hora_fin, 'HH:mm:ss') : null,
        ...(esEventoPersonal ? {
          paciente_id: null,
          especialidad_id: null,
          etapa_id: null,
          tipo: values.tipo || 'Otro'
        } : {})
      };

      const fechasFormateadas = selectedDates.map(date => format(date, 'yyyy-MM-dd'));
      const dataToSend = {
        ...baseData,
        fechas: fechasFormateadas
      };

   

      const response = await citaService.createMultiplesCitas(dataToSend);
      
      if (response.data.conflictos && response.data.conflictos.length > 0) {
        showConflictDialog(response.data);
      } else {
        showNotification(
          `${response.data.totalCreadas} ${esEventoPersonal ? 'eventos' : 'citas'} creadas exitosamente`,
          'success'
        );
        navigate('/citas');
      }
    } catch (error) {
      console.error('  Error creando múltiples citas:', error);
      
      if (error.response?.status === 409 && error.response?.data?.conflictos) {
        showConflictDialog(error.response.data);
      } else {
        showNotification(
          `Error al crear las ${esEventoPersonal ? 'eventos' : 'citas'}`,
          'error'
        );
      }
    } finally {
      setCreatingMultiple(false);
    }
  };

  // Mostrar diÃ¡logo de conflictos
  const showConflictDialog = (conflictData) => {
    setCitasToCreate(conflictData);
    setPreviewDialogOpen(true);
  };

  // Confirmar creación con conflictos
  const handleConfirmWithConflicts = async () => {
    try {
      setCreatingMultiple(true);
      
      if (citasToCreate.fechasValidas && citasToCreate.fechasValidas.length > 0) {
        const baseData = {
          paciente_id: formik.values.paciente_id,
          especialista_id: formik.values.especialista_id,
          especialidad_id: formik.values.especialidad_id,
          etapa_id: formik.values.etapa_id,
          es_evento_personal: esEventoPersonal,
          hora_inicio: formik.values.hora_inicio ? format(formik.values.hora_inicio, 'HH:mm:ss') : null,
          hora_fin: formik.values.hora_fin ? format(formik.values.hora_fin, 'HH:mm:ss') : null,
          tipo: formik.values.tipo,
          estado: formik.values.estado,
          motivo: formik.values.motivo,
          notas: formik.values.notas,
          fechas: citasToCreate.fechasValidas
        };

        const response = await citaService.createMultiplesCitas(baseData);
        
        showNotification(
          `${response.data.totalCreadas} ${esEventoPersonal ? 'eventos' : 'citas'} creadas exitosamente. ${citasToCreate.conflictos.length} fechas con conflictos fueron omitidas.`,
          'warning'
        );
        
        navigate('/citas');
      }
    } catch (error) {
      console.error('  Error confirmando citas:', error);
      showNotification('Error al crear las citas', 'error');
    } finally {
      setCreatingMultiple(false);
      setPreviewDialogOpen(false);
    }
  };

  // ===== VALIDACIóN Y FORMIK =====

  //   Esquema de validación dinÃ¡mico actualizado
  const validationSchema = Yup.object({
  especialista_id: Yup.number().required('El especialista es requerido'),
  
  fecha: Yup.date().nullable(),
  
  paciente_id: Yup.number().when([], {
    is: () => !esEventoPersonal && !esCitaGrupal && !esCitaGrupalRecurrente,
    then: (schema) => schema.required('El paciente es requerido'),
    otherwise: (schema) => schema.nullable()
  }),

  especialidad_id: Yup.number().when('es_evento_personal', {
    is: false,
    then: (schema) => schema.required('La especialidad es requerida'),
    otherwise: (schema) => schema.nullable()
  }),
  
  etapa_id: Yup.number().when('es_evento_personal', {
    is: false,
    then: (schema) => schema.required('La etapa es requerida'),
    otherwise: (schema) => schema.nullable()
  }),
  
  motivo: Yup.string(),
  notas: Yup.string()
});

  // Formik setup
  const formik = useFormik({
  initialValues: {
    paciente_id: '',
    especialista_id: '',
    especialidad_id: '',
    etapa_id: '',
    fecha: fechaFromUrl ? new Date(fechaFromUrl) : null,
    selectedDates: [],
    multipleFechas: false,
    hora_inicio: null,
    hora_fin: null,
    tipo: 'Seguimiento',
    estado: 'Programada',
    motivo: '',
    notas: '',
    es_evento_personal: false,
    es_cita_grupal: false,
    es_cita_recurrente: false,
    pacientes_seleccionados: []
  },
  validationSchema,
  
  onSubmit: async (values) => {
 
  const tipoCreacion = determinarTipoCreacion();
   
  switch (tipoCreacion) {
    case 'grupal-recurrente':
       await handleCreateCitasRecurrentes(values);
      break;
      
    case 'individual-recurrente':
       await handleCreateCitasRecurrentes(values);
      break;
      
    case 'grupal-simple':
       await handleCreateCitaGrupal(values);
      break;
      
    case 'grupal-multiple':
       await handleCreateCitaGrupalMultipleFechas(values, pacientesSeleccionados.map(p => p.id));
      break;
      
    case 'grupal-scheduler':
       await handleCreateCitaGrupalFromScheduler(values, pacientesSeleccionados.map(p => p.id));
      break;
      
    case 'scheduler':
       await handleCreateMultipleCitasFromScheduler(values);
      break;
      
    case 'multiple-fechas':
       await handleCreateMultipleCitas(values);
      break;
      
    default:
       await handleCreateSingleCita(values);
      break;
  }
}
});

const handleMultipleFechasToggle = useCallback((event) => {
  const isMultiple = event.target.checked;
  setMultipleFechas(isMultiple);
  formik.setFieldValue('multipleFechas', isMultiple);
  
  if (isMultiple) {
    formik.setFieldValue('fecha', null);
    if (formik.values.fecha) {
      setSelectedDates([formik.values.fecha]);
      formik.setFieldValue('selectedDates', [formik.values.fecha]);
    }
  } else {
    if (selectedDates.length > 0) {
      formik.setFieldValue('fecha', selectedDates[0]);
    }
    setSelectedDates([]);
    formik.setFieldValue('selectedDates', []);
  }
}, [formik, selectedDates]);

const handleCitaGrupalChange = (event) => {
  const esCitaGrupal = event.target.checked;
  
   
  setEsCitaGrupal(esCitaGrupal);
  formik.setFieldValue('es_cita_grupal', esCitaGrupal);
  
  if (esCitaGrupal) {
    // Limpiar paciente individual
    formik.setFieldValue('paciente_id', '');
    setSelectedPaciente(null);
    
    // Desactivar evento personal si estaba activo
    setEsEventoPersonal(false);
    formik.setFieldValue('es_evento_personal', false);
    
    // Limpiar slots del scheduler
    setSchedulerSelectedSlots([]);
    
    // Inicializar array de pacientes
    formik.setFieldValue('pacientes_seleccionados', []);
    setPacientesSeleccionados([]);
  } else {
    // Limpiar selección múltiple
    formik.setFieldValue('pacientes_seleccionados', []);
    setPacientesSeleccionados([]);
    setGrupoInfo({ nombre: '', descripcion: '' });
  }
};

const handlePacientesMultiplesChange = useCallback((event, newValue) => {
   
  const pacientesActualizados = newValue || [];
  
  setPacientesSeleccionados(pacientesActualizados);
  formik.setFieldValue('pacientes_seleccionados', pacientesActualizados);
  
  // Auto-generar nombre de grupo si estÃ¡ vacío
  if (pacientesActualizados.length >= 2 && !grupoInfo.nombre.trim()) {
    const tipoGrupo = esCitaGrupalRecurrente ? 'Grupo Recurrente' : 'Grupo';
    setGrupoInfo(prev => ({
      ...prev,
      nombre: `${tipoGrupo} - ${format(new Date(), 'MMM yyyy')}`,
      descripcion: esCitaGrupalRecurrente ? 
        'Citas grupales recurrentes semanales' : 
        'Cita grupal programada'
    }));
  }
}, [esCitaGrupalRecurrente, grupoInfo.nombre]);


  // ===== EFECTOS =====

  // Cargar datos iniciales - SOLO UNA VEZ
  useEffect(() => {
  const cargarDatosIniciales = async () => {
    try {
      setLoadingData(true);
      
      const especialistasRes = await especialistaService.getEspecialistas();
      setEspecialistas(especialistasRes.data || []);
      
      //  • SI HAY PACIENTE EN URL, CARGARLO
      if (pacienteIdFromUrl && !isEdit) {
        await cargarPacienteDesdeUrl(parseInt(pacienteIdFromUrl));
      }
      
    } catch (error) {
      console.error('  Error cargando datos iniciales:', error);
      showNotification('Error al cargar datos iniciales', 'error');
    } finally {
      setLoadingData(false);
    }
  };
  
  cargarDatosIniciales();
}, [showNotification, pacienteIdFromUrl, isEdit, cargarPacienteDesdeUrl]);

// POR ESTE (SIN cargarPacienteDesdeUrl en dependencias):
useEffect(() => {
  const cargarDatosIniciales = async () => {
    try {
      setLoadingData(true);
      
      const especialistasRes = await especialistaService.getEspecialistas();
      setEspecialistas(especialistasRes.data || []);
      
    } catch (error) {
      console.error('  Error cargando datos iniciales:', error);
      showNotification('Error al cargar datos iniciales', 'error');
    } finally {
      setLoadingData(false);
    }
  };
  
  cargarDatosIniciales();
}, [showNotification]);

useEffect(() => {
  if (pacienteIdFromUrl && !isEdit && !loadingData && especialistas.length > 0) {
     cargarPacienteDesdeUrl(parseInt(pacienteIdFromUrl));
  }
}, [pacienteIdFromUrl, isEdit, loadingData, especialistas.length]);

  useEffect(() => {
 

  if (pacienteFromUrl && !isEdit && !loading) {
     
    // Sincronizar paciente
    if (formik.values.paciente_id !== pacienteFromUrl.id) {
      formik.setFieldValue('paciente_id', pacienteFromUrl.id);
     }
    
    // Sincronizar especialista
    if (selectedEspecialista && formik.values.especialista_id !== selectedEspecialista.id) {
      formik.setFieldValue('especialista_id', selectedEspecialista.id);
     }
    
    //  • Sincronizar especialidad
    if (selectedEspecialidad && formik.values.especialidad_id !== selectedEspecialidad.id) {
      formik.setFieldValue('especialidad_id', selectedEspecialidad.id);
     }
  }
}, [pacienteFromUrl, selectedEspecialista, selectedEspecialidad, isEdit, loading]);

  // Cargar cita para edición - SOLO si es edición
  useEffect(() => {
    if (!isEdit || !id) return;
    
    const cargarCitaParaEdicion = async () => {
      try {
        setLoading(true);
        const response = await citaService.getCita(id);
        const cita = response.data;
 
        
        formik.setValues({
          paciente_id: cita.paciente_id || '',
          especialista_id: cita.especialista_id || '',
          especialidad_id: cita.especialidad_id || '',
          etapa_id: cita.etapa_id || '',
          fecha: cita.fecha ? new Date(cita.fecha) : null,
          hora_inicio: cita.hora_inicio ? parse(cita.hora_inicio, 'HH:mm:ss', new Date()) : null,
          hora_fin: cita.hora_fin ? parse(cita.hora_fin, 'HH:mm:ss', new Date()) : null,
          tipo: cita.tipo || 'Seguimiento',
          estado: cita.estado || 'Programada',
          motivo: cita.motivo || '',
          notas: cita.notas || '',
          es_evento_personal: cita.es_evento_personal || false,
          selectedDates: [],
          multipleFechas: false
        });
        
        setEsEventoPersonal(cita.es_evento_personal || false);
        
        if (cita.paciente) setSelectedPaciente(cita.paciente);
        if (cita.especialista) {
          setSelectedEspecialista(cita.especialista);
          if (cita.especialista.especialidades) {
            setEspecialidadesFiltradas(cita.especialista.especialidades);
          }
        }
        if (cita.especialidad) setSelectedEspecialidad(cita.especialidad);
        
        if (cita.especialista_id && !cita.es_evento_personal) {
          await cargarPacientesDelEspecialista(cita.especialista_id);
        }
        
        if (cita.especialidad_id) {
          await cargarEtapasDeEspecialidad(cita.especialidad_id, cita.paciente_id);
        }
 
      } catch (error) {
        console.error('  Error cargando cita:', error);
        showNotification('Error al cargar los datos de la cita', 'error');
        navigate('/citas');
      } finally {
        setLoading(false);
      }
    };
    
    cargarCitaParaEdicion();
  }, [id, isEdit, navigate, showNotification]);

  // ===== COMPONENTES AUXILIARES =====

  // Componente de información de sesión
  const InfoSesion = () => {
    if (!formik.values.etapa_id || esEventoPersonal) return null;
    
    return (
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <AssignmentIcon color="primary" />
          <Box>
            <Typography variant="subtitle2" color="primary">
              Información de la Sesión
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {calculandoSesion ? (
                <Skeleton width={100} />
              ) : (
                `Sesión número ${numeroSesion} de esta etapa`
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    );
  };

  // ===== RENDER =====

  // Mostrar loading mientras carga datos
  if (loadingData || loading || loadingPacienteFromUrl) {
    return (
      <Box display="flex" justifyContent="center" my={5}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      <PageHeader 
        title={isEdit ? 'Editar Cita' : 'Nueva Cita'}
        breadcrumbs={[
          { text: 'Dashboard', to: '/dashboard' },
          { text: 'Citas', to: '/citas' },
          { text: isEdit ? 'Editar Cita' : 'Nueva Cita' }
        ]}
      />
      
      {pacienteFromUrl && !isEdit && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body1">
            <strong>ðŸ“‹ Datos precargados para:</strong> {pacienteFromUrl.nombres} {pacienteFromUrl.apellidos}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Historia Clínica: {pacienteFromUrl.numero_historia}
            {selectedEspecialista && ` | Especialista: ${selectedEspecialista.usuario?.nombre} ${selectedEspecialista.usuario?.apellidos}`}
            {selectedEspecialidad && ` | Especialidad: ${selectedEspecialidad.nombre}`}
          </Typography>
          {/*  • MOSTRAR ESTADO DE DEBUG */}
          <Typography variant="caption" sx={{ display: 'block', mt: 1, fontFamily: 'monospace', bgcolor: 'rgba(0,0,0,0.1)', p: 0.5, borderRadius: 1 }}>
            Debug: Especialista ID: {selectedEspecialista?.id || 'No cargado'} | Especialidad ID: {selectedEspecialidad?.id || 'No cargada'}
          </Typography>
        </Alert>
      )}

      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3}>
          {/* Card principal */}
          <Grid item xs={12}>
            <Card elevation={2}>
              <CardContent>
                <Stack direction="row" alignItems="center" spacing={2} mb={3}>
                  <EventIcon color="primary" />
                  <Typography variant="h6">
                    {esEventoPersonal ? 'Evento Personal/Administrativo' : 'Nueva Cita Medica'}
                  </Typography>
                </Stack>
                
<Box mb={3}>
  <Typography variant="subtitle1" color="primary" fontWeight="bold" mb={2}>
    Tipo de Cita
  </Typography>
  
  {/* Opción 1: Evento Personal/Administrativo */}
  <FormControlLabel
    control={
      <Switch
        checked={esEventoPersonal}
        onChange={(event) => {
          const isEventoPersonal = event.target.checked;
          setEsEventoPersonal(isEventoPersonal);
          formik.setFieldValue('es_evento_personal', isEventoPersonal);
          
          if (isEventoPersonal) {
            // Limpiar todo lo relacionado con pacientes
            setEsCitaGrupal(false);
            setEsCitaRecurrente(false);
            setEsCitaGrupalRecurrente(false);
            setMultipleFechas(false);
            
            formik.setFieldValue('es_cita_grupal', false);
            formik.setFieldValue('es_cita_recurrente', false);
            formik.setFieldValue('multipleFechas', false);
            
            formik.setFieldValue('paciente_id', '');
            formik.setFieldValue('especialidad_id', '');
            formik.setFieldValue('etapa_id', '');
            formik.setFieldValue('tipo', 'Otro');
            
            setSelectedPaciente(null);
            setSelectedEspecialidad(null);
            setPacientes([]);
            setEtapas([]);
            setEspecialidadesFiltradas([]);
            setPacientesSeleccionados([]);
            setSchedulerSelectedSlots([]);
            setSelectedDates([]);
            setPatronesRecurrentes([]);
            setFechaInicioRecurrencia(null);
          } else {
            formik.setFieldValue('tipo', 'Seguimiento');
            if (selectedEspecialista?.id) {
              if (selectedEspecialista.especialidades) {
                setEspecialidadesFiltradas(selectedEspecialista.especialidades);
              }
              cargarPacientesDelEspecialista(selectedEspecialista.id);
            }
          }
        }}
        name="es_evento_personal"
        color="primary"
      />
    }
    label={
      <Box>
        <Typography variant="body1">
          Evento Personal/Administrativo
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Activa esta opción para reuniones, capacitaciones o eventos no relacionados con pacientes
        </Typography>
      </Box>
    }
    sx={{ width: '100%', mb: 2 }}
  />

  {/* Opciones solo disponibles si NO es evento personal */}
  {!esEventoPersonal && (
    <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'divider' }}>
      
      {/* SIMPLIFICADA: Citas Grupales Recurrentes */}
      <FormControlLabel
        control={
          <Switch
            checked={esCitaGrupalRecurrente}
            onChange={(event) => {
              const isGrupalRecurrente = event.target.checked;
              setEsCitaGrupalRecurrente(isGrupalRecurrente);
              formik.setFieldValue('es_cita_grupal_recurrente', isGrupalRecurrente);
              
              if (isGrupalRecurrente) {
                // âœ… ACTIVAR automÃ¡ticamente el modo grupal
                setEsCitaGrupal(true);
                formik.setFieldValue('es_cita_grupal', true);
                
                // âœ… DESACTIVAR todas las demÃ¡s opciones incompatibles
                setEsCitaRecurrente(false);
                setMultipleFechas(false);
                
                formik.setFieldValue('es_cita_recurrente', false);
                formik.setFieldValue('multipleFechas', false);
                
                // Limpiar paciente individual
                formik.setFieldValue('paciente_id', '');
                setSelectedPaciente(null);
                
                // Limpiar elementos incompatibles
                setSelectedDates([]);
                setSchedulerSelectedSlots([]);
                
                formik.setFieldValue('fecha', null);
                formik.setFieldValue('hora_inicio', null);
                formik.setFieldValue('hora_fin', null);
                
               } else {
                // Al desactivar, limpiar configuraciones recurrentes
                setPatronesRecurrentes([]);
                setFechaInicioRecurrencia(null);
                
                // Mantener modo grupal normal
               }
            }}
            name="es_cita_grupal_recurrente"
            color="success"
          />
        }
        label={
          <Box>
            <Typography variant="body1" color="success.main">
              Citas Grupales Recurrentes
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Múltiples pacientes con patrón semanal recurrente - Configuración completa
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip 
                label="MODO COMPLETO" 
                size="small" 
                color="success" 
                variant="filled"
                sx={{ fontSize: '0.6rem', height: 18 }}
              />
            </Box>
          </Box>
        }
        sx={{ width: '100%' }}
      />

      {/* âœ… MOSTRAR otras opciones SOLO si NO estÃ¡ en modo grupal recurrente */}
      {!esCitaGrupalRecurrente && (
        <>
          {/* Opción 2: Cita Grupal Simple */}
          <FormControlLabel
            control={
              <Switch
                checked={esCitaGrupal && !esCitaGrupalRecurrente}
                onChange={(event) => {
                  const isGrupal = event.target.checked;
                  setEsCitaGrupal(isGrupal);
                  formik.setFieldValue('es_cita_grupal', isGrupal);
                  
                  if (isGrupal) {
                    // Desactivar incompatibles
                    setEsCitaRecurrente(false);
                    setMultipleFechas(false);
                    
                    formik.setFieldValue('es_cita_recurrente', false);
                    formik.setFieldValue('multipleFechas', false);
                    
                    // Limpiar paciente individual
                    formik.setFieldValue('paciente_id', '');
                    setSelectedPaciente(null);
                    
                    // Limpiar recurrencia
                    setPatronesRecurrentes([]);
                    setFechaInicioRecurrencia(null);
                    setSelectedDates([]);
                    setSchedulerSelectedSlots([]);
                  } else {
                    // Limpiar datos grupales
                    setPacientesSeleccionados([]);
                    setGrupoInfo({ nombre: '', descripcion: '' });
                    formik.setFieldValue('pacientes_seleccionados', []);
                  }
                }}
                name="es_cita_grupal"
                color="secondary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" color="secondary">
                  Cita Grupal Simple
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Múltiples pacientes en el mismo horario (una sola fecha)
                </Typography>
              </Box>
            }
            sx={{ width: '100%', mb: 1.5 }}
          />

          {/* Opción 3: Múltiples fechas */}
          {!hasSchedulerSlots && !esCitaRecurrente && (
            <FormControlLabel
              control={
                <Switch
                  checked={multipleFechas}
                  onChange={handleMultipleFechasToggle}
                  name="multipleFechas"
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" color="warning.main">
                    Programar en múltiples fechas
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {esCitaGrupal 
                      ? `Misma cita grupal para ${pacientesSeleccionados.length || 'varios'} pacientes en varias fechas`
                      : 'Crea la misma cita en varias fechas diferentes con las mismas condiciones'
                    }
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', mb: 1.5 }}
            />
          )}

          {/* Opción 4: Programación Recurrente Individual */}
          {!multipleFechas && (
            <FormControlLabel
              control={
                <Switch
                  checked={esCitaRecurrente && !esCitaGrupalRecurrente}
                  onChange={(event) => {
                    const isRecurrente = event.target.checked;
                    setEsCitaRecurrente(isRecurrente);
                    formik.setFieldValue('es_cita_recurrente', isRecurrente);
                    
                    if (isRecurrente) {
                      // Desactivar incompatibles
                      setMultipleFechas(false);
                      
                      formik.setFieldValue('multipleFechas', false);
                      
                      // Limpiar incompatibles
                      setSelectedDates([]);
                      setSchedulerSelectedSlots([]);
                      
                      formik.setFieldValue('fecha', null);
                      formik.setFieldValue('hora_inicio', null);
                      formik.setFieldValue('hora_fin', null);
                    } else {
                      setPatronesRecurrentes([]);
                      setFechaInicioRecurrencia(null);
                    }
                  }}
                  name="es_cita_recurrente"
                  color="info"
                />
              }
              label={
                <Box>
                  <Typography variant="body1" color="info.main">
                    Programación Recurrente Individual
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Programa citas individuales que se repiten semanalmente
                  </Typography>
                </Box>
              }
              sx={{ width: '100%', mb: 1.5 }}
            />
          )}
        </>
      )}
    </Box>
  )}
</Box>
     
                
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={3}>
                  {/* Sección: Especialista */}
                  <Grid item xs={12}>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                      <PersonIcon color="primary" fontSize="small" />
                      <Typography variant="subtitle1" color="primary" fontWeight="bold">
                        Especialista
                      </Typography>
                    </Stack>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Autocomplete
                          value={selectedEspecialista}
                          onChange={handleEspecialistaChange}
                          options={especialistas}
                          isOptionEqualToValue={(option, value) => option.id === value.id}
                          getOptionLabel={(option) => 
                            option.usuario 
                              ? `${option.usuario.nombre} ${option.usuario.apellidos}`
                              : 'Especialista desconocido'
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Seleccionar Especialista"
                              error={formik.touched.especialista_id && Boolean(formik.errors.especialista_id)}
                              helperText={formik.touched.especialista_id && formik.errors.especialista_id}
                            />
                          )}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  {/*  • AvailabilityScheduler - NUEVA INTEGRACI óN */}
                   {selectedEspecialista && 
 !esEventoPersonal && 
 !esCitaRecurrente && 
 !esCitaGrupalRecurrente && (
  <Grid item xs={12}>
    <AvailabilityScheduler
      selectedEspecialista={selectedEspecialista}
      selectedSlots={schedulerSelectedSlots}
      onSlotsChange={(newSlots) => {
        setSchedulerSelectedSlots(newSlots);
        // ... mantÃ©n el resto del código onSlotsChange igual
      }}
      onCitaClick={(cita) => {
        navigate(`/citas/editar/${cita.id}`);
      }}
      disabled={loadingData || loading || !selectedEspecialista}
    />
  </Grid>
)}
                  

                  {/*  • RecurrenceScheduler - Solo si estÃ¡ activado el modo recurrente */}
                  {(esCitaRecurrente || esCitaGrupalRecurrente) && selectedEspecialista && (
                    <Grid item xs={12}>
                      <Paper 
  elevation={0}
  sx={{ 
    p: 3, 
    bgcolor: (theme) => alpha(esCitaGrupalRecurrente ? theme.palette.success.main : theme.palette.info.main, 0.1),
    border: (theme) => `2px solid ${alpha(esCitaGrupalRecurrente ? theme.palette.success.main : theme.palette.info.main, 0.3)}`,
    borderRadius: 2
  }}
>
  <Stack direction="row" alignItems="center" spacing={2} mb={2}>
    <ScheduleIcon color={esCitaGrupalRecurrente ? "success" : "info"} />
    <Typography variant="h6" color={esCitaGrupalRecurrente ? "success.main" : "info.main"}>
      {esCitaGrupalRecurrente ? 
        'Configuración de Citas Grupales Recurrentes' : 
        'Configuración de Programación Recurrente'
      }
    </Typography>
  </Stack>

                      <RecurrenceScheduler
                        patrones={patronesRecurrentes}
                        onPatronesChange={setPatronesRecurrentes}
                        fechaInicio={fechaInicioRecurrencia}
                        onFechaInicioChange={setFechaInicioRecurrencia}
                        disabled={loadingData || !selectedEspecialista}
                        pacientesSeleccionados={esCitaGrupalRecurrente ? pacientesSeleccionados : []}
                        // âœ… NUEVA PROP para indicar modo
                        isGrupalMode={esCitaGrupalRecurrente}
                      />
                      </Paper>
                    </Grid>
                  )}

                  {/*  • Resumen de horarios seleccionados del scheduler */}
                  {hasSchedulerSlots && (
                    <Grid item xs={12}>
                      <Paper 
                        elevation={0}
                        sx={{ 
                          p: 2, 
                          bgcolor: (theme) => alpha(theme.palette.success.main, 0.1),
                          border: (theme) => `1px solid ${alpha(theme.palette.success.main, 0.2)}`
                        }}
                      >
                        <Stack direction="row" spacing={2} alignItems="center">
                          <CheckIcon color="success" />
                          <Box>
                            <Typography variant="subtitle2" color="success.main">
                              âœ… Horarios Seleccionados desde el Calendario
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {schedulerSelectedSlots.length} horario{schedulerSelectedSlots.length > 1 ? 's' : ''} seleccionado{schedulerSelectedSlots.length > 1 ? 's' : ''} 
                              {schedulerSelectedSlots.length > 1 ? ' - Se crearÃ¡n múltiples citas' : ' - Se crearÃ¡ una cita'}
                            </Typography>
                          </Box>
                        </Stack>
                      </Paper>
                    </Grid>
                  )}

                  {/* Sección: Información del Paciente - Solo para citas normales */}
                 {!esEventoPersonal && (
  <Grid item xs={12}>
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      <HospitalIcon color="primary" fontSize="small" />
      <Typography variant="subtitle1" color="primary" fontWeight="bold">
        Información del Paciente
      </Typography>
    </Stack>
    
    <Grid container spacing={2}>
      {/* Paciente */}
      <Grid item xs={12} md={(esCitaGrupal || esCitaGrupalRecurrente) ? 12 : 6}>
        {(esCitaGrupal || esCitaGrupalRecurrente) ? (
            // Selector múltiple para citas grupales
            <Autocomplete
              multiple
              value={pacientesSeleccionados}
              onChange={handlePacientesMultiplesChange}
              options={pacientes}
              loading={loadingPacientes}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              getOptionLabel={(option) => 
                `${option.nombres} ${option.apellidos} (${option.numero_historia})`
              }
              disabled={!formik.values.especialista_id}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Seleccionar Pacientes para Cita Grupal"
                  error={formik.touched.pacientes_seleccionados && Boolean(formik.errors.pacientes_seleccionados)}
                  helperText={
                    formik.touched.pacientes_seleccionados && formik.errors.pacientes_seleccionados ||
                    (!formik.values.especialista_id ? 'Selecciona un especialista primero' : 
                    `${pacientesSeleccionados.length} paciente(s) seleccionado(s)`)
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingPacientes ? <CircularProgress color="inherit" size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderTags={(tagValue, getTagProps) =>
                tagValue.map((option, index) => (
                  <Chip
                    label={`${option.nombres} ${option.apellidos}`}
                    {...getTagProps({ index })}
                    key={option.id}
                    size="small"
                    color={esCitaGrupalRecurrente ? "success" : "secondary"}
                    variant="outlined"
                  />
                ))
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box>
                    <Typography variant="body2">
                      {option.nombres} {option.apellidos}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      HC: {option.numero_historia}
                    </Typography>
                  </Box>
                </Box>
              )}
            />
        ) : (
          // Selector individual original
          <Autocomplete
            value={selectedPaciente}
            onChange={handlePacienteChange}
            options={pacientes}
            loading={loadingPacientes}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            getOptionLabel={(option) => 
              `${option.nombres} ${option.apellidos} (${option.numero_historia})`
            }
            disabled={!formik.values.especialista_id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Seleccionar Paciente"
                error={formik.touched.paciente_id && Boolean(formik.errors.paciente_id)}
                helperText={
                  formik.touched.paciente_id && formik.errors.paciente_id ||
                  (!formik.values.especialista_id ? 'Selecciona un especialista primero' : '')
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingPacientes ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}
      </Grid>
                        
                        {/* Especialidad */}
                        <Grid item xs={12} md={6}>
                          <FormControl 
                            fullWidth
                            error={formik.touched.especialidad_id && Boolean(formik.errors.especialidad_id)}
                          >
                            <InputLabel>Especialidad</InputLabel>
                            <Select
                              name="especialidad_id"
                              value={formik.values.especialidad_id || ''}
                              onChange={handleEspecialidadChange}
                              onBlur={formik.handleBlur}
                              label="Especialidad"
                              disabled={!selectedEspecialista}
                            >
                              <MenuItem value="">
                                <em>Seleccionar especialidad...</em>
                              </MenuItem>
                              {especialidadesFiltradas.map((esp) => (
                                <MenuItem key={esp.id} value={esp.id}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Chip 
                                      label={esp.nombre} 
                                      size="small" 
                                      variant="outlined"
                                      sx={{ color: esp.color, borderColor: esp.color }}
                                    />
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                            {formik.touched.especialidad_id && formik.errors.especialidad_id && (
                              <FormHelperText>{formik.errors.especialidad_id}</FormHelperText>
                            )}
                            {!selectedEspecialista && (
                              <FormHelperText>Selecciona un especialista primero</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        
                        {/* Etapa de tratamiento */}
                        <Grid item xs={12} md={6}>
                          <FormControl 
                            fullWidth
                            error={formik.touched.etapa_id && Boolean(formik.errors.etapa_id)}
                          >
                            <InputLabel>Etapa de Tratamiento</InputLabel>
                            <Select
                              name="etapa_id"
                              value={formik.values.etapa_id || ''}
                              onChange={handleEtapaChange}
                              onBlur={formik.handleBlur}
                              disabled={!formik.values.especialidad_id}
                              label="Etapa de Tratamiento"
                              endAdornment={loadingEtapas && <CircularProgress size={20} />}
                            >
                              <MenuItem value="">
                                <em>Seleccionar etapa...</em>
                              </MenuItem>
                              {etapas.map((etapa) => (
                                <MenuItem key={etapa.id} value={etapa.id}>
                                  <Stack direction="row" spacing={1} alignItems="center">
                                    <Typography>{etapa.nombre}</Typography>
                                    {etapa.duracion_estimada && (
                                      <Chip 
                                        label={`${etapa.duracion_estimada} ses.`} 
                                        size="small" 
                                        color="info"
                                        variant="outlined"
                                      />
                                    )}
                                  </Stack>
                                </MenuItem>
                              ))}
                            </Select>
                            {formik.touched.etapa_id && formik.errors.etapa_id && (
                              <FormHelperText>{formik.errors.etapa_id}</FormHelperText>
                            )}
                            {!formik.values.especialidad_id && (
                              <FormHelperText>Selecciona una especialidad primero</FormHelperText>
                            )}
                          </FormControl>
                        </Grid>
                        
                        {/* Información de sesión */}
                        <Grid item xs={12} md={6}>
                          <InfoSesion />
                        </Grid>

                        {(esCitaGrupal || esCitaGrupalRecurrente) && pacientesSeleccionados.length > 0 && (
  <>
    {/* âœ… RESUMEN MEJORADO para citas grupales recurrentes */}
    <Grid item xs={12}>
      <Paper 
        elevation={0}
        sx={{ 
          p: 2, 
          bgcolor: (theme) => alpha(esCitaGrupalRecurrente ? theme.palette.success.main : theme.palette.secondary.main, 0.1),
          border: (theme) => `1px solid ${alpha(esCitaGrupalRecurrente ? theme.palette.success.main : theme.palette.secondary.main, 0.2)}`
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <PersonIcon color={esCitaGrupalRecurrente ? "success" : "secondary"} />
          <Box>
            <Typography variant="subtitle2" color={esCitaGrupalRecurrente ? "success" : "secondary"}>
              âœ… {esCitaGrupalRecurrente ? 'Cita Grupal Recurrente Configurada' : 'Cita Grupal Configurada'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {pacientesSeleccionados.length} pacientes seleccionados: {' '}
              {pacientesSeleccionados.map(p => `${p.nombres} ${p.apellidos}`).join(', ')}
              {esCitaGrupalRecurrente && patronesRecurrentes.length > 0 && (
                <>
                  <br />
                  ðŸ“… {patronesRecurrentes.reduce((total, patron) => total + patron.cantidadSesiones, 0)} sesiones programadas en patrón recurrente
                </>
              )}
            </Typography>
          </Box>
        </Stack>
      </Paper>
    </Grid>

    {/* Campos opcionales del grupo */}
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Nombre del Grupo (Opcional)"
        value={grupoInfo.nombre}
        onChange={(e) => setGrupoInfo(prev => ({ ...prev, nombre: e.target.value }))}
        placeholder={esCitaGrupalRecurrente ? 
          "Ej: Terapia Grupal Ansiedad - Ciclo Marzo 2025" :
          "Ej: Terapia Familiar García, Grupo Ansiedad Marzo 2025"
        }
        helperText={esCitaGrupalRecurrente ? 
          "Nombre para identificar este ciclo de citas grupales recurrentes" :
          "Nombre identificativo para este grupo de pacientes"
        }
      />
    </Grid>
    
    <Grid item xs={12} md={6}>
      <TextField
        fullWidth
        label="Descripción del Grupo (Opcional)"
        value={grupoInfo.descripcion}
        onChange={(e) => setGrupoInfo(prev => ({ ...prev, descripcion: e.target.value }))}
        placeholder={esCitaGrupalRecurrente ?
          "Ej: Terapia grupal semanal para manejo de ansiedad" :
          "Ej: Sesión de terapia familiar, Grupo de apoyo"
        }
        helperText={esCitaGrupalRecurrente ?
          "Descripción del tipo de terapia grupal recurrente" :
          "Descripción breve del tipo de cita grupal"
        }
      />
    </Grid>
  </>
)}

                      </Grid>
                    </Grid>
                  )}
                  
                  {/*   Sección: Programación - Solo mostrar si NO hay slots del scheduler seleccionados O es evento personal */}
                  {(!hasSchedulerSlots || esEventoPersonal) && !esCitaRecurrente && (
                    <Grid item xs={12}>
                      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <ScheduleIcon color="primary" fontSize="small" />
                        <Typography variant="subtitle1" color="primary" fontWeight="bold">
                          Programación
                        </Typography>
                      </Stack>
                      
                      <Grid container spacing={2}>
                        {/* Selector de fechas - Solo si NO hay slots del scheduler */}
                        {!hasSchedulerSlots && (
                          <Grid item xs={12} md={multipleFechas ? 8 : 4}>
                            {multipleFechas ? (
                              // Selector de múltiples fechas
                              <MultipleDatePicker
                                selectedDates={selectedDates}
                                onDatesChange={handleMultipleDatesChange}
                                especialistaId={formik.values.especialista_id}
                                horaInicio={formik.values.hora_inicio}
                                horaFin={formik.values.hora_fin}
                                disabled={!formik.values.especialista_id}
                              />
                            ) : (
                              // Selector de fecha única (código original)
                              <DatePicker
                                label="Fecha"
                                value={formik.values.fecha}
                                onChange={(date) => formik.setFieldValue('fecha', date)}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    error: formik.touched.fecha && Boolean(formik.errors.fecha),
                                    helperText: formik.touched.fecha && formik.errors.fecha
                                  }
                                }}
                              />
                            )}
                          </Grid>
                        )}

                        {/* Mostrar resumen si hay múltiples fechas del selector manual */}
                        {multipleFechas && selectedDates.length > 0 && !hasSchedulerSlots && (
                          <Grid item xs={12} md={4}>
                            <Paper 
                              elevation={0}
                              sx={{ 
                                p: 2, 
                                bgcolor: (theme) => alpha(theme.palette.secondary.main, 0.1),
                                border: (theme) => `1px solid ${alpha(theme.palette.secondary.main, 0.2)}`
                              }}
                            >
                              <Stack direction="row" spacing={2} alignItems="center">
                                <CalendarIcon color="secondary" />
                                <Box>
                                  <Typography variant="subtitle2" color="secondary">
                                    Fechas Seleccionadas
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                    {selectedDates.length} cita{selectedDates.length > 1 ? 's' : ''} a crear
                                  </Typography>
                                </Box>
                              </Stack>
                            </Paper>
                          </Grid>
                        )}
                        
                        {/* Hora Inicio - Solo si hay una fecha seleccionada Y NO hay slots del scheduler */}
                        {(formik.values.fecha || esEventoPersonal) && !hasSchedulerSlots && (
                          <Grid item xs={12} md={4}>
                            <TimePicker
                              label="Hora de Inicio"
                              value={formik.values.hora_inicio}
                              onChange={handleHoraInicioChange}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: formik.touched.hora_inicio && Boolean(formik.errors.hora_inicio),
                                  helperText: formik.touched.hora_inicio && formik.errors.hora_inicio
                                }
                              }}
                            />
                          </Grid>
                        )}
                        
                        {/* Hora Fin - Solo si hay hora de inicio Y NO hay slots del scheduler */}
                        {formik.values.hora_inicio && !hasSchedulerSlots && (
                          <Grid item xs={12} md={4}>
                            <TimePicker
                              label="Hora de Fin"
                              value={formik.values.hora_fin}
                              onChange={(hora) => formik.setFieldValue('hora_fin', hora)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  error: formik.touched.hora_fin && Boolean(formik.errors.hora_fin),
                                  helperText: formik.touched.hora_fin && formik.errors.hora_fin
                                }
                              }}
                            />
                          </Grid>
                        )}
                        
                        {/* Tipo - Solo para eventos personales */}
                        {esEventoPersonal && (
                          <Grid item xs={12} md={4}>
                            <FormControl fullWidth>
                              <InputLabel>Tipo de Evento</InputLabel>
                              <Select
                                name="tipo"
                                value={formik.values.tipo}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                label="Tipo de Evento"
                              >
                                <MenuItem value="Reunión">Reunión</MenuItem>
                                <MenuItem value="Descanso">Descanso</MenuItem>
                                <MenuItem value="Capacitación">Capacitación</MenuItem>
                                <MenuItem value="Otro">Otro</MenuItem>
                              </Select>
                            </FormControl>
                          </Grid>
                        )}
                        
                        {/* Estado */}
                        <Grid item xs={12} md={4}>
                          <FormControl fullWidth>
                            <InputLabel>Estado</InputLabel>
                            <Select
                              name="estado"
                              value={formik.values.estado}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              label="Estado"
                            >
                              <MenuItem value="Programada">Programada</MenuItem>
                              <MenuItem value="Confirmada">Confirmada</MenuItem>
                              <MenuItem value="Cancelada">Cancelada</MenuItem>
                              <MenuItem value="Realizada">Realizada</MenuItem>
                              <MenuItem value="No asistió">No asistió</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>
                    </Grid>
                  )}
                  
                  {/* Sección: Detalles */}
                  <Grid item xs={12}>
                    <Typography variant="subtitle1" color="primary" fontWeight="bold" mb={2}>
                      Detalles Adicionales
                    </Typography>
                    
                    <Grid container spacing={2}>
                      {/* Motivo */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="motivo"
                          label={esEventoPersonal ? "Descripción del evento" : "Motivo de la Cita"}
                          value={formik.values.motivo}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.motivo && Boolean(formik.errors.motivo)}
                          helperText={formik.touched.motivo && formik.errors.motivo}
                          multiline
                          rows={2}
                          placeholder={esEventoPersonal ? "Describe brevemente el evento o actividad..." : "Describe el motivo de la consulta..."}
                        />
                      </Grid>
                      
                      {/* Notas */}
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          name="notas"
                          label="Notas Adicionales"
                          value={formik.values.notas}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.notas && Boolean(formik.errors.notas)}
                          helperText={formik.touched.notas && formik.errors.notas}
                          multiline
                          rows={3}
                          placeholder="Información adicional o instrucciones especiales..."
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
          
          {/* Botones de acción */}
          <Grid item xs={12}>
            <Paper elevation={1} sx={{ p: 2 }}>
              <Stack direction="row" justifyContent="flex-end" spacing={2}>
                <Button
                  variant="outlined"
                  color="secondary"
                  onClick={() => navigate('/citas')}
                  disabled={formik.isSubmitting || creatingMultiple}
                >
                  Cancelar
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={formik.isSubmitting || creatingMultiple}
                  size="large"
                >
                   
                {formik.isSubmitting || creatingMultiple ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={20} color="inherit" />
                    <Typography>
                      {creatingMultiple ? 'Creando citas...' : 'Guardando...'}
                    </Typography>
                  </Stack>
                ) : isEdit ? (
                  'Actualizar Cita'
                ) : (esCitaRecurrente || esCitaGrupalRecurrente) ? (
                  // âœ… TEXTO MEJORADO PARA RECURRENTES
                  (() => {
                    const totalSesiones = patronesRecurrentes.reduce((total, patron) => total + patron.cantidadSesiones, 0);
                    return esCitaGrupalRecurrente 
                      ? `Crear ${totalSesiones} Sesiones Grupales Recurrentes (${pacientesSeleccionados.length} pacientes c/u)`
                      : `Crear ${totalSesiones} Citas Recurrentes`;
                  })()
                ) : (esCitaGrupal || esCitaGrupalRecurrente) ? (
                  // Texto para citas grupales
                  hasSchedulerSlots ? (
                    `Crear ${schedulerSelectedSlots.length} Citas Grupales desde Calendario (${pacientesSeleccionados.length} pacientes c/u)`
                  ) : multipleFechas ? (
                    `Crear ${selectedDates.length} Citas Grupales (${pacientesSeleccionados.length} pacientes c/u)`
                  ) : (
                    `Crear Cita Grupal (${pacientesSeleccionados.length} pacientes)`
                  )
                ) : hasSchedulerSlots ? (
                  `Crear ${schedulerSelectedSlots.length} Citas Desde Calendario`
                ) : multipleFechas ? (
                  `Crear ${selectedDates.length} Citas`
                ) : (
                  'Crear Cita'
                )}

                </Button>
              </Stack>
            </Paper>
          </Grid>
        </Grid>
      </form>

      {/* DiÃ¡logo de preview de conflictos */}
      <Dialog
        open={previewDialogOpen}
        onClose={() => setPreviewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" spacing={1}>
            <WarningIcon color="warning" />
            <Typography variant="h6">
              Conflictos Detectados
            </Typography>
          </Stack>
        </DialogTitle>
        <DialogContent>
          {citasToCreate && (
            <Box>
              <Alert severity="warning" sx={{ mb: 3 }}>
                <Typography variant="body1" gutterBottom>
                  <strong>Se detectaron conflictos en {citasToCreate.conflictos?.length || 0} de {citasToCreate.totalSolicitadas || 0} fechas solicitadas.</strong>
                </Typography>
                <Typography variant="body2">
                  Puedes crear las citas en las fechas disponibles y revisar manualmente los conflictos.
                </Typography>
              </Alert>

              {/* Fechas con conflictos */}
              {citasToCreate.conflictos && citasToCreate.conflictos.length > 0 && (
                <Box mb={3}>
                  <Typography variant="subtitle1" color="error" gutterBottom>
                    Fechas con Conflictos ({citasToCreate.conflictos.length})
                  </Typography>
                  <Stack spacing={1}>
                    {citasToCreate.conflictos.map((conflicto, index) => (
                      <Alert key={index} severity="error" variant="outlined">
                        <Typography variant="body2">
                          <strong>{format(new Date(conflicto.fecha), 'dd/MM/yyyy')}</strong>: {conflicto.error}
                        </Typography>
                      </Alert>
                    ))}
                  </Stack>
                </Box>
              )}

              {/* Fechas vÃ¡lidas */}
              {citasToCreate.fechasValidas && citasToCreate.fechasValidas.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" color="success.main" gutterBottom>
                    Fechas Disponibles ({citasToCreate.fechasValidas.length})
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
                    {citasToCreate.fechasValidas.map((fecha, index) => (
                      <Chip
                        key={index}
                        icon={<CheckCircleIcon />}
                        label={format(new Date(fecha), 'dd/MM/yyyy')}
                        color="success"
                        variant="outlined"
                      />
                    ))}
                  </Stack>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setPreviewDialogOpen(false)}
            disabled={creatingMultiple}
          >
            Cancelar
          </Button>
          {citasToCreate?.fechasValidas?.length > 0 && (
            <Button
              onClick={handleConfirmWithConflicts}
              variant="contained"
              color="primary"
              disabled={creatingMultiple}
            >
              {creatingMultiple ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={16} color="inherit" />
                  <span>Creando...</span>
                </Stack>
              ) : (
                `Crear ${citasToCreate.fechasValidas.length} Citas Disponibles`
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CitaForm;